/**
 * Dashboard static data.
 *
 * Provides static configuration for dashboard module tiles.
 *
 * @module dashboardData
 */

import { SHOW_STUDY_PARTNER_FINDER } from '@/lib/featureFlags';
import { ROUTES } from '@/lib/routeConstants';

/**
 * Action button configuration.
 *
 * Represents a dashboard module tile with a route and optional badge state.
 */
export interface DashboardModule {
  id: string;
  icon: string;
  route?: string;
  disabled?: boolean;
  /** Shown in the sidebar tooltip when plan access disables the module. */
  disabledHint?: string;
  badge?: 'new';
}

/**
 * Dashboard module configuration.
 *
 * Defines the simplified dashboard tiles displayed on the dashboard.
 */
const ALL_DASHBOARD_MODULES: DashboardModule[] = [
  {
    id: 'learning-materials',
    icon: 'GraduationCap',
    route: ROUTES.LEARNING,
  },
  {
    id: 'exam-simulation',
    icon: 'ClipboardList',
    route: ROUTES.EXAM,
  },
  {
    id: 'find-study-partner',
    icon: 'Users',
    route: ROUTES.PARTNERS,
  },
  {
    id: 'analysis-and-results',
    icon: 'BarChart3',
    route: ROUTES.RESULTS,
  },
  {
    id: 'feedback',
    icon: 'MessageSquare',
    route: ROUTES.FEEDBACK,
  },
  {
    id: 'human-reviews',
    icon: 'UserCheck',
    route: ROUTES.HUMAN_REVIEWS,
  },
];

/** Module IDs temporarily excluded from sidebar / quick-access navigation. */
const NAV_HIDDEN_MODULE_IDS = new Set<string>([
  'learning-materials',
  ...(SHOW_STUDY_PARTNER_FINDER ? [] : ['find-study-partner']),
]);

export const dashboardModules: DashboardModule[] = ALL_DASHBOARD_MODULES.filter(
  (module) => !NAV_HIDDEN_MODULE_IDS.has(module.id),
);
